// src/FatalErrorPage/FatalErrorPage.tsx
import * as React from "react";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Header from "@cloudscape-design/components/header";
import TextContent from "@cloudscape-design/components/text-content";
import Button from "@cloudscape-design/components/button";
import Link from "@cloudscape-design/components/link";

// src/FatalErrorPage/error_icon.svg
var error_icon_default = 'data:image/svg+xml,<svg width="124" height="79" viewBox="0 0 124 79" fill="none" xmlns="http://www.w3.org/2000/svg">%0A<path d="M9.16406 4.75001C9.16406 2.54087 10.9549 0.75 13.1641 0.75H110.022C112.232 0.75 114.022 2.54086 114.022 4.75V70.3092H9.16406V4.75001Z" fill="%23F0FBFF" stroke="%230F141A" stroke-width="1.5"/>%0A<path d="M0.75 71.1875C0.75 70.6352 1.19772 70.1875 1.75 70.1875H121.438C121.99 70.1875 122.438 70.6352 122.438 71.1875V72.75C122.438 75.5114 120.199 77.75 117.438 77.75H5.75C2.98858 77.75 0.75 75.5114 0.75 72.75V71.1875Z" fill="%23F3F3F7" stroke="%230F141A" stroke-width="1.5"/>%0A<path d="M53 70.7604C53 70.444 53.2565 70.1875 53.5729 70.1875H70.3021C70.6185 70.1875 70.875 70.444 70.875 70.7604V70.7604C70.875 72.3425 69.5925 73.625 68.0104 73.625H55.8646C54.2825 73.625 53 72.3425 53 70.7604V70.7604Z" stroke="%230F141A" stroke-width="1.5"/>%0A<g clip-path="url(%23clip0_4417_229158)">%0A<path d="M61.7486 56.3155C73.0669 56.3155 82.2345 47.1368 82.2345 35.8276C82.2345 24.5185 73.0568 15.3398 61.7486 15.3398C50.4405 15.3398 41.2627 24.5185 41.2627 35.8276C41.2627 47.1368 50.4405 56.3155 61.7486 56.3155Z" fill="%23DB0000" stroke="%230F141A" stroke-width="1.5" stroke-miterlimit="10"/>%0A<path d="M65.0517 35.8278L70.051 30.9247L66.811 27.6211L61.7475 32.5872L56.6841 27.6211L53.444 30.9247L58.4433 35.8278L53.4434 40.7316L56.6834 44.0352L61.7475 39.0685L66.8116 44.0352L70.0517 40.7316L65.0517 35.8278Z" fill="white" stroke="%230F141A" stroke-width="1.5" stroke-linecap="round"/>%0A</g>%0A<defs>%0A<clipPath id="clip0_4417_229158">%0A<rect width="42" height="42" fill="white" transform="translate(40.75 14.8281)"/>%0A</clipPath>%0A</defs>%0A</svg>%0A';

// ../../tool-configs/awsmp-max-monitoring-frontend/lib/metrics-constants.js
var CustomOpsMetricName;
(function(CustomOpsMetricName2) {
  CustomOpsMetricName2["ClientSide404"] = "ClientSide404";
  CustomOpsMetricName2["ClientSide500"] = "ClientSide500";
  CustomOpsMetricName2["ClientSideNonFatalToolCallError"] = "ClientSideNonFatalToolCallError";
  CustomOpsMetricName2["EPOBuyerUnonboardedPageRender"] = "EPOBuyerUnonboardedPageRender";
})(CustomOpsMetricName || (CustomOpsMetricName = {}));
var CustomOpsSdkServiceName;
(function(CustomOpsSdkServiceName2) {
  CustomOpsSdkServiceName2["MARKETPLACE_DISCOVERY"] = "MarketplaceDiscovery";
  CustomOpsSdkServiceName2["MARKETPLACE_AGREEMENT"] = "MarketplaceAgreement";
  CustomOpsSdkServiceName2["MARKETPLACE_CATALOG"] = "MarketplaceCatalog";
})(CustomOpsSdkServiceName || (CustomOpsSdkServiceName = {}));

// ../../tool-configs/awsmp-max-monitoring-frontend/lib/sdk-metrics/types.js
var STATUS_CODE_CLASS;
(function(STATUS_CODE_CLASS2) {
  STATUS_CODE_CLASS2["TWO_HUNDRED"] = "200";
  STATUS_CODE_CLASS2["FOUR_HUNDRED"] = "400";
  STATUS_CODE_CLASS2["FIVE_HUNDRED"] = "500";
})(STATUS_CODE_CLASS || (STATUS_CODE_CLASS = {}));

// src/FatalErrorPage/FatalErrorPage.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var FatalErrorPage = ({ logger }) => {
  React.useEffect(() => {
    logger?.opsMetric({
      metricName: CustomOpsMetricName.ClientSide500,
      unit: "Count",
      value: 1
    });
  }, []);
  const handleRefresh = () => {
    window.location.reload();
  };
  return /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "center", width: "100%", minHeight: "50vh", alignItems: "center" }, children: /* @__PURE__ */ jsx(Box, { padding: "xl", children: /* @__PURE__ */ jsxs(SpaceBetween, { size: "l", direction: "vertical", alignItems: "start", children: [
    /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx("img", { src: error_icon_default, alt: "Error", style: { width: "122px", height: "77px" } }) }),
    /* @__PURE__ */ jsx(Header, { variant: "h1", children: "Something went wrong" }),
    /* @__PURE__ */ jsx(TextContent, { children: "We're experiencing an internal error. Try refreshing the page, and if the problem continues, contact support." }),
    /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Button, { iconName: "refresh", onClick: handleRefresh, children: "Refresh page" }) }),
    /* @__PURE__ */ jsxs(TextContent, { children: [
      /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx(Link, { variant: "primary", href: "/", external: true, children: "Agent Mode" }) }),
      /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx(Link, { variant: "primary", href: "https://aws.amazon.com/marketplace/", external: true, children: "AWS Marketplace Home" }) }),
      /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx(Link, { variant: "primary", href: "https://aws.amazon.com/marketplace/help/contact-us", external: true, children: "Contact AWS Marketplace" }) })
    ] })
  ] }) }) });
};
export {
  FatalErrorPage
};
//# sourceMappingURL=index.js.map
